// Lazy-loaded Calendly popup helper.
//
// Loads the Calendly widget script + CSS on first use (one time per page),
// then opens the inline popup overlay so the visitor stays on drive-hr.com
// while booking. If the Calendly script fails to load for any reason
// (offline, ad blocker, CDN hiccup), falls back to opening the booking page
// in a new tab so a visitor can always reach the form.

const SCRIPT_SRC = 'https://assets.calendly.com/assets/external/widget.js';
const STYLE_HREF = 'https://assets.calendly.com/assets/external/widget.css';

let loaderPromise = null;

const ensureCalendlyLoaded = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  if (window.Calendly) return Promise.resolve(window.Calendly);
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise((resolve, reject) => {
    // Inject the stylesheet once.
    if (!document.querySelector(`link[href="${STYLE_HREF}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = STYLE_HREF;
      document.head.appendChild(link);
    }

    // Inject the widget script once.
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing && window.Calendly) {
      resolve(window.Calendly);
      return;
    }

    const script = existing || document.createElement('script');
    if (!existing) {
      script.src = SCRIPT_SRC;
      script.async = true;
      document.head.appendChild(script);
    }

    const onReady = () => {
      if (window.Calendly) resolve(window.Calendly);
      else reject(new Error('Calendly script loaded but window.Calendly is missing'));
    };
    script.addEventListener('load', onReady);
    script.addEventListener('error', () => reject(new Error('Failed to load Calendly script')));
  });

  return loaderPromise;
};

/**
 * Open the Calendly popup widget for the given booking URL. If the widget
 * cannot be loaded, opens the URL in a new tab so the user can still book.
 *
 * @param {string} url Calendly event URL, e.g. https://calendly.com/drive-hr/30min
 */
export const openCalendlyPopup = async (url) => {
  try {
    const Calendly = await ensureCalendlyLoaded();
    Calendly.initPopupWidget({ url });
  } catch (_err) {
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }
};

export const CALENDLY_BOOKING_URL = 'https://calendly.com/drive-hr/30min';
